import { Button, notification, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DeleteOutlined,
  EditOutlined,
  RedoOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { editingLocked } from '../../../../utils/editingLocked';
import { routes } from '../../../../utils/constants/adminRoutes';
import { useActionsButtons } from '../../utils/useActionsButtons';
import { useSocketContext } from '../../../../contexts/SocketContext';
import { GenerateSynthesisButton } from './GenerateSynthesisButton';

/**
 * RequestButtons component renders a set of buttons for handling supervision actions.
 * @component
 *
 * @param {Object} props - The component props.
 * @param {string} props.supervisionId - The ID of the supervision.
 * @param {Function} props.setRefreshData - Function to refresh data.
 *
 * @returns {JSX.Element} The rendered component.
 */
export const RequestButtons = ({ supervisionId, setRefreshData }) => {
  const { t } = useTranslation();
  const { handlePatchStatus, handleDelete } = useActionsButtons({
    supervisionId,
    setRefreshData
  });
  const { editSupervisionLock } = useSocketContext();

  return (
    <>
      <Button type="primary" onClick={handlePatchStatus}>
        <RedoOutlined />
        {`${t('supervisions.show.buttons.confirm_supervision')} `}
      </Button>
      <GenerateSynthesisButton supervisionId={supervisionId} />
      <Link
        to={{
          pathname: !editingLocked(editSupervisionLock, supervisionId)
            ? `${routes.SUPERVISIONS}/edit/${supervisionId}`
            : ''
        }}
      >
        <Button
          type="primary"
          onClick={() =>
            editingLocked(editSupervisionLock, supervisionId) &&
            notification.warning({
              message: t('pensioners.profile_lock'),
              duration: false
            })
          }
        >
          {`${t('buttons.edit')} `}
          <EditOutlined />
        </Button>
      </Link>
      <Popconfirm
        title={t('datatable.column.action.archive.title')}
        okText={t('datatable.column.action.archive.ok')}
        okButtonProps={{ type: 'danger' }}
        cancelText={t('datatable.column.action.archive.cancel')}
        onConfirm={handleDelete}
        icon={<WarningOutlined />}
      >
        <Button type="link" danger>
          {`${t('buttons.archive')} `}
          <DeleteOutlined />
        </Button>
      </Popconfirm>
    </>
  );
};

RequestButtons.propTypes = {
  supervisionId: PropTypes.string.isRequired,
  setRefreshData: PropTypes.func.isRequired
};
